import { cn } from "@/lib/utils";
import { formatDualPrice } from "@/lib/marketing-pricing";
import { localizePriceLabel } from "@/lib/localize-price-label";
import { m } from "@/paraglide/messages.js";

type BookingSitePriceCardPrice = {
  label: string;
  cash_cents: number;
  card_cents: number;
  duration_minutes: number | null;
  lesson_count: number | null;
};

type Props = {
  sitePrice: BookingSitePriceCardPrice;
  onSelect: () => void;
  selected?: boolean;
  isPackage?: boolean;
  /** Member uses Paraglide; Admin stays English per i18n rule. */
  variant?: "member" | "admin";
};

export function BookingSitePriceCard({
  sitePrice,
  onSelect,
  selected = false,
  isPackage,
  variant = "member",
}: Props) {
  const eyebrow =
    isPackage && sitePrice.lesson_count && sitePrice.duration_minutes
      ? variant === "member"
        ? m.booking_card_lessons_min({
            count: sitePrice.lesson_count,
            minutes: sitePrice.duration_minutes,
          })
        : `${sitePrice.lesson_count} lessons · ${sitePrice.duration_minutes} min`
      : sitePrice.duration_minutes
        ? variant === "member"
          ? m.booking_card_min({ minutes: sitePrice.duration_minutes })
          : `${sitePrice.duration_minutes} min`
        : "";

  if (variant === "admin") {
    return (
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className="rounded-lg border border-border p-4 text-left hover:border-accent"
      >
        <div className="text-xs text-accent">{eyebrow}</div>
        <div className="mt-1 font-medium">{sitePrice.label}</div>
        <div className="mt-2 font-display text-lg text-accent">
          {formatDualPrice(sitePrice.cash_cents, sitePrice.card_cents)}
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        "rounded-xl border p-6 text-start transition-all hover:border-accent hover:shadow-md",
        selected ? "border-accent ring-2 ring-accent/30" : "border-border bg-card",
      )}
    >
      <div className="text-xs font-semibold uppercase tracking-wider text-accent">{eyebrow}</div>
      <div className="mt-1 font-display text-lg font-semibold">
        {/* DB site_prices labels are English; map known ones like /prices does (#123). */}
        {localizePriceLabel(sitePrice.label)}
      </div>
      <div className="mt-3 font-display text-2xl font-bold text-accent">
        {formatDualPrice(sitePrice.cash_cents, sitePrice.card_cents)}
      </div>
      {isPackage && (
        <p className="mt-2 text-xs text-muted-foreground">{m.booking_card_book_first()}</p>
      )}
    </button>
  );
}
